import { Chip, Link, Stack, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import type { InvoiceEntity } from "../../../../api/types";
import { DateDisplay } from "../../../../components/DateDisplay";
import { CopyableText } from "../../../../components/CopyableText";
import { invoiceListAmount } from "../../../../logic/invoice-amount";
import { AdminTileCard } from "../../../../components/AdminList";
import { SenderHeading } from "../../../../components/SenderHeading";
import { InvoiceActions } from "./InvoiceActions";

export type InvoiceTileProps = {
  item: InvoiceEntity;
  onEdit: (item: InvoiceEntity) => void;
  onView: (item: InvoiceEntity) => void;
  onViewTransactions: (item: InvoiceEntity) => void;
};

export const InvoiceTile = ({
  item,
  onEdit,
  onView,
  onViewTransactions,
}: InvoiceTileProps) => {
  const attachmentCount = item.attachments?.length ?? 0;
  const amount = invoiceListAmount(item);
  return (
    <AdminTileCard
      footer={
        <InvoiceActions
          size="small"
          item={item}
          onEdit={onEdit}
          onView={onView}
          onViewTransactions={onViewTransactions}
        />
      }
    >
      <SenderHeading invoice={item} size="md" />
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => onEdit(item)}
        aria-label={`Edit invoice ${item.name}`}
        title={item.name}
        underline="hover"
        sx={{
          fontWeight: 600,
          textAlign: "left",
          color: "text.primary",
          minHeight: 32,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.name}
      </Link>
      {item.description && (
        <Typography
          variant="caption"
          sx={{ fontStyle: "italic", color: "text.secondary" }}
        >
          {item.description}
        </Typography>
      )}
      <Stack
        direction="row"
        spacing={2}
        sx={{ flexWrap: "wrap", color: "text.secondary" }}
      >
        {amount && (
          <Typography variant="caption">
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {amount.main}
            </Typography>
            {amount.minimum && (
              <Typography
                component="span"
                variant="caption"
                // One unit, so a narrow tile wraps "min 1 030,00 kr" whole
                // onto the next line instead of breaking after "min".
                sx={{
                  ml: 0.75,
                  color: "text.secondary",
                  display: "inline-block",
                  whiteSpace: "nowrap",
                }}
              >
                {amount.minimum}
              </Typography>
            )}
          </Typography>
        )}
        <Typography variant="caption">
          Due <DateDisplay date={item.dueDate} />
        </Typography>
        <Typography variant="caption">
          Received <DateDisplay date={item.dateReceived} />
        </Typography>
      </Stack>
      {attachmentCount > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
          <Chip
            size="small"
            icon={<AttachFileIcon />}
            label={`${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`}
            variant="outlined"
          />
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">
        <CopyableText text={item.id} maxLength={10} />
      </Typography>
    </AdminTileCard>
  );
};
